import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { Modal } from '../../../../../components/ui/Modal';
import { Button } from '../../../../../components/ui/Button';
import { FileDropzone } from '../../../../../components/ui/FileDropzone';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import { uploadCatalog } from '../../../../../features/catalog/catalogThunks';
import {
  CATALOG_TEMPLATE_COLUMNS,
  CATALOG_TEMPLATE_EXAMPLE,
} from '../../../../../features/catalog/catalogConstants';

/** The server reads the sheet with openpyxl, which does not open the old `.xls`. */
const SHEET_EXTENSIONS = ['.xlsx'];

const downloadTemplate = () => {
  const sheet = XLSX.utils.json_to_sheet([CATALOG_TEMPLATE_EXAMPLE], {
    header: CATALOG_TEMPLATE_COLUMNS,
  });
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, 'Productos');
  XLSX.writeFile(book, 'plantilla-productos.xlsx');
};

/**
 * Loads a sheet of products into ONE client's catalog.
 *
 * The client is declared here and not in the sheet, on purpose: the permission
 * is checked once and no cell can contradict it. The load is an upsert and all
 * or nothing, and the dialog says both before anything is sent — a sheet that
 * quietly updates forty products is not what somebody adding three expects.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {Array<{value: number, label: string}>} props.clientOptions
 * @param {number|null} props.defaultCompanyId - The client the listing is filtered by.
 * @param {(message: string) => void} props.onDone - Told the server's own summary.
 */
export const CatalogUploadModal = ({ isOpen, onClose, clientOptions, defaultCompanyId, onDone }) => {
  const dispatch = useDispatch();
  const uploading = useSelector((state) => state.catalog.uploading);

  const [companyId, setCompanyId] = useState(null);
  const [file, setFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);

  const onlyClientId = clientOptions.length === 1 ? clientOptions[0].value : null;

  // Every opening starts without a file, on the client the listing is on.
  useEffect(() => {
    if (!isOpen) return;
    setCompanyId(defaultCompanyId ?? onlyClientId);
    setFile(null);
    setUploadError(null);
  }, [isOpen, defaultCompanyId, onlyClientId]);

  const companyName = clientOptions.find((option) => option.value === companyId)?.label;

  const problem = (() => {
    if (!companyId) return 'Elige el cliente';
    if (!file) return 'Elige la planilla';
    return null;
  })();

  const handleUpload = async () => {
    setUploadError(null);
    const action = await dispatch(uploadCatalog({ companyId, file }));

    if (action.meta.requestStatus !== 'fulfilled') {
      // Nothing went in. The sheet stays staged so it is clear which one failed.
      setUploadError(action.payload);
      return;
    }

    onDone(action.payload.message);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Carga masiva de productos"
      testId="catalog-upload-modal"
    >
      <p className="text-sm text-text-secondary text-center -mt-4 mb-6">
        {companyName
          ? `Al catálogo de ${companyName}`
          : 'Elige a qué cliente le vas a cargar el catálogo'}
      </p>

      <div className="space-y-4">
        {uploadError && (
          <div
            data-testid="catalog-upload-error"
            className="bg-red-50 dark:bg-red-900/20 text-danger p-3 rounded-xl border border-red-200 dark:border-red-900 text-sm"
          >
            {uploadError} No se cargó ninguna fila.
          </div>
        )}

        {clientOptions.length > 1 && (
          <FilterableSelect
            variant="form"
            testId="catalog-upload-client"
            label="Cliente"
            value={companyId}
            onChange={setCompanyId}
            options={clientOptions}
            placeholder="Seleccionar..."
            searchPlaceholder="Buscar cliente..."
            disabled={uploading}
          />
        )}

        <FileDropzone
          file={file}
          onFileSelected={(selected) => {
            setFile(selected);
            setUploadError(null);
          }}
          onClear={() => setFile(null)}
          extensions={SHEET_EXTENSIONS}
          hint="Planilla .xlsx con las columnas SKU y Nombre; el resto es opcional"
          disabled={uploading}
        />

        <div className="text-xs text-text-muted space-y-1">
          <p>
            Columnas: <strong>SKU</strong> y <strong>Nombre</strong> obligatorias. EAN, Precio,
            Peso, Unidad, Lote, Vencimiento, Serie (sí o no) y Rotación (A, B o C) opcionales.
          </p>
          <p>
            Un SKU que ya existe se actualiza; el que no, se crea. Es todo o nada: si una fila
            falla, no entra ninguna.
          </p>
        </div>

        <Button
          variant="ghost"
          size="sm"
          icon={Download}
          onClick={downloadTemplate}
          data-testid="catalog-upload-template"
        >
          Descargar plantilla
        </Button>
      </div>

      <div className="flex justify-end gap-3 mt-8">
        <Button variant="ghost" onClick={onClose} disabled={uploading}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          loading={uploading}
          loadingLabel="Cargando..."
          disabled={Boolean(problem)}
          onClick={handleUpload}
          data-testid="catalog-upload-submit"
        >
          {problem || 'Cargar planilla'}
        </Button>
      </div>
    </Modal>
  );
};
